import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import { useModalContext } from '@costar-ui/share-context';
import { FocusScope } from '@react-aria/focus';
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import ModalManager, { ariaHidden } from './modal-manager.esm.js';
import TrapFocus from './trap-focus.esm.js';
import useCallbackRef from '../../../hooks/use-callback-ref.esm.js';
import useForkRef from '../../../hooks/use-fork-ref.esm.js';
import useTimeout from '../../../hooks/use-timeout.esm.js';
import { isNumeric } from '../../../utils/assertion.esm.js';
import { callAll } from '../../../utils/function.esm.js';
import ownerDocument from '../../../utils/owner-document.esm.js';
import { BoxPrivate } from '../../_primitives/box/box.esm.js';
import Backdrop from '../backdrop/backdrop.esm.js';
import { useContainerContext } from '../container-context.esm.js';
import Portal from '../portal/portal.esm.js';

var _excluded = ["BackdropComponent", "BackdropProps", "useModalAsBackdrop", "children", "closeAfterTransition", "container", "disableAutoFocus", "disableBackdropClick", "disableEnforceFocus", "disableEscapeKeyDown", "disablePortal", "shouldHoistTheme", "disableRestoreFocus", "disableScrollLock", "scrollBarWidth", "hideBackdrop", "keepMounted", "zIndex", "adornerComponent", "onBackdropClick", "onClose", "onEscapeKeyDown", "open", "hostContainerElement"],
  _excluded2 = ["onEnter", "onExited", "tabIndex"];
function getContainer(container) {
  container = typeof container === 'function' ? container() : container;
  // eslint-disable-next-line react/no-find-dom-node
  return ReactDOM.findDOMNode(container);
}
function getHasTransition(props) {
  return props.children && props.children.hasOwnProperty('props') ? props.children.props.hasOwnProperty('in') : false;
}

// A modal manager used to track and manage the state of open Modals.
// Modals don't open on the server so this won't conflict with concurrent requests.
var defaultManager = new ModalManager();
var defaultZIndices = 'modal';

/**
 * Modal is a lower-level construct that is leveraged by the following components:
 *
 * - [Dialog](/api/dialog/)
 * - [Drawer](/api/drawer/)
 * - [Menu](/api/menu/)
 * - [Popover](/api/popover/)
 *
 * If you are creating a modal dialog, you probably want to use the [Dialog](/api/dialog/) component
 * rather than directly using Modal.
 *
 * This component shares many concepts with [react-overlays](https://react-bootstrap.github.io/react-overlays/#modals).
 */
var Modal = /*#__PURE__*/React.forwardRef(function Modal(props, ref) {
  var _useContainerContext, _children$props;
  var _props$BackdropCompon = props.BackdropComponent,
    BackdropComponent = _props$BackdropCompon === void 0 ? Backdrop : _props$BackdropCompon,
    BackdropProps = props.BackdropProps,
    useModalAsBackdrop = props.useModalAsBackdrop,
    children = props.children,
    _props$closeAfterTran = props.closeAfterTransition,
    closeAfterTransition = _props$closeAfterTran === void 0 ? false : _props$closeAfterTran,
    container = props.container,
    _props$disableAutoFoc = props.disableAutoFocus,
    disableAutoFocus = _props$disableAutoFoc === void 0 ? false : _props$disableAutoFoc,
    _props$disableBackdro = props.disableBackdropClick,
    disableBackdropClick = _props$disableBackdro === void 0 ? false : _props$disableBackdro,
    _props$disableEnforce = props.disableEnforceFocus,
    disableEnforceFocus = _props$disableEnforce === void 0 ? false : _props$disableEnforce,
    _props$disableEscapeK = props.disableEscapeKeyDown,
    disableEscapeKeyDown = _props$disableEscapeK === void 0 ? false : _props$disableEscapeK,
    _props$disablePortal = props.disablePortal,
    disablePortal = _props$disablePortal === void 0 ? false : _props$disablePortal,
    _props$shouldHoistThe = props.shouldHoistTheme,
    shouldHoistTheme = _props$shouldHoistThe === void 0 ? true : _props$shouldHoistThe,
    _props$disableRestore = props.disableRestoreFocus,
    disableRestoreFocus = _props$disableRestore === void 0 ? false : _props$disableRestore,
    _props$disableScrollL = props.disableScrollLock,
    disableScrollLock = _props$disableScrollL === void 0 ? false : _props$disableScrollL,
    scrollBarWidth = props.scrollBarWidth,
    _props$hideBackdrop = props.hideBackdrop,
    hideBackdrop = _props$hideBackdrop === void 0 ? false : _props$hideBackdrop,
    _props$keepMounted = props.keepMounted,
    keepMounted = _props$keepMounted === void 0 ? false : _props$keepMounted,
    _props$zIndex = props.zIndex,
    zIndexProp = _props$zIndex === void 0 ? defaultZIndices : _props$zIndex,
    adornerComponent = props.adornerComponent,
    onBackdropClick = props.onBackdropClick,
    onClose = props.onClose,
    onEscapeKeyDown = props.onEscapeKeyDown,
    open = props.open,
    _hostContainerElement = props.hostContainerElement,
    rest = _objectWithoutProperties(props, _excluded);
  var _ref = useModalContext() || {},
    _manager = _ref.manager;
  var manager = _manager || defaultManager;
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    exited = _useState2[0],
    setExited = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    mounted = _useState4[0],
    setMounted = _useState4[1];
  var modal = useRef({});
  var mountNodeRef = useRef();
  var modalRef = useRef(null);
  var handleRef = useForkRef(modalRef, ref);
  var hasTransition = getHasTransition(props);
  var _ref2 = (_useContainerContext = useContainerContext()) !== null && _useContainerContext !== void 0 ? _useContainerContext : {},
    containerRef = _ref2.containerRef;
  var hostContainerElement = _hostContainerElement !== null && _hostContainerElement !== void 0 ? _hostContainerElement : containerRef === null || containerRef === void 0 ? void 0 : containerRef.current;
  var getDoc = useCallback(function () {
    return ownerDocument(mountNodeRef.current);
  }, []);
  var getModal = function getModal() {
    modal.current.modalRef = modalRef.current;
    modal.current.mountNode = mountNodeRef.current;
    return modal.current;
  };
  var handleMounted = function handleMounted() {
    manager.mount(getModal(), {
      disableScrollLock: disableScrollLock,
      scrollBarWidth: scrollBarWidth
    });
    if (modalRef.current) {
      // Fix a bug on Chrome where the scroll isn't initially 0.
      modalRef.current.scrollTop = 0;
    }
    setMounted(true);
  };
  var handleOpen = useCallbackRef(function () {
    var resolvedContainer = getContainer(container) || getDoc().body;
    manager.add(getModal(), resolvedContainer);

    // The element was already mounted.
    if (modalRef.current) {
      handleMounted();
    }
  });
  var isTopModal = useCallback(function () {
    return manager.isTopModal(getModal());
  }, [manager]);
  var handlePortalRef = useCallbackRef(function (node) {
    mountNodeRef.current = node;
    if (!node) {
      return;
    }
    if (open && isTopModal()) {
      handleMounted();
    } else {
      ariaHidden(modalRef.current, true);
    }
  });
  var handleClose = useCallback(function () {
    manager.remove(getModal());
    setMounted(false);
  }, [manager]);
  useEffect(function () {
    return function () {
      handleClose();
    };
  }, [handleClose]);
  useEffect(function () {
    if (open) {
      handleOpen();
    } else if (!hasTransition || !closeAfterTransition) {
      handleClose();
    }
  }, [open, handleClose, hasTransition, closeAfterTransition, handleOpen]);
  var delayExecution = useTimeout(5);
  useEffect(function () {
    if (mounted && disableScrollLock && open) {
      var handleResizeOrScroll = function handleResizeOrScroll() {
        handleClose();
        if (onClose) {
          onClose({}, 'scrollOrResize');
        }
      };
      var win = undefined;
      // delay subscription to allow ui scroll settle
      delayExecution(function () {
        if (hostContainerElement) {
          hostContainerElement.addEventListener('scroll', handleResizeOrScroll);
          win = ownerDocument(modal.current.mountNode);
          win.addEventListener('resize', handleResizeOrScroll);
        } else {
          win = ownerDocument(modal.current.mountNode).body;
          win.addEventListener('resize', handleResizeOrScroll);
          win.addEventListener('scroll', handleResizeOrScroll);
        }
      });
      return function () {
        if (hostContainerElement) {
          hostContainerElement.removeEventListener('scroll', handleResizeOrScroll);
          if (win) {
            win.removeEventListener('resize', handleResizeOrScroll);
          }
        }
        if (win) {
          win.removeEventListener('resize', handleResizeOrScroll);
          win.removeEventListener('scroll', handleResizeOrScroll);
        }
      };
    }
    return;
  }, [delayExecution, disableScrollLock, handleClose, mounted, onClose, open, hostContainerElement]);
  var handleEnter = useCallback(function () {
    setExited(false);
  }, []);
  var handleExited = useCallback(function () {
    setExited(true);
    if (closeAfterTransition) {
      handleClose();
    }
  }, [closeAfterTransition, handleClose]);
  var handleBackdropClick = useCallback(function (event) {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (onBackdropClick) {
      onBackdropClick(event);
    }
    if (!disableBackdropClick && onClose) {
      onClose(event, 'backdropClick');
    }
  }, [disableBackdropClick, onBackdropClick, onClose]);
  var handleKeyDown = useCallback(function (event) {
    // The handler doesn't take event.defaultPrevented into account:
    //
    // event.preventDefault() is meant to stop default behaviors like
    // clicking a checkbox to check it, hitting a button to submit a form,
    // and hitting left arrow to move the cursor in a text input etc.
    // Only special HTML elements have these default behaviors.
    if (event.key !== 'Escape' || !isTopModal()) {
      return;
    }

    // Swallow the event, in case someone is listening for the escape key on the body.
    event.stopPropagation();
    if (onEscapeKeyDown) {
      onEscapeKeyDown(event);
    }
    if (!disableEscapeKeyDown && onClose) {
      onClose(event, 'escapeKeyDown');
    }
  }, [disableEscapeKeyDown, isTopModal, onClose, onEscapeKeyDown]);
  var _ref3 = (_children$props = children === null || children === void 0 ? void 0 : children.props) !== null && _children$props !== void 0 ? _children$props : {},
    childrenOnEnter = _ref3.onEnter,
    childrenOnExited = _ref3.onExited,
    childrenTabIndex = _ref3.tabIndex,
    restChildrenProps = _objectWithoutProperties(_ref3, _excluded2);
  var childProps = useMemo(function () {
    return _objectSpread2({
      onKeyDown: handleKeyDown,
      tabIndex: childrenTabIndex || '-1'
    }, hasTransition ? {
      onEnter: callAll(handleEnter, childrenOnEnter),
      onExited: callAll(handleExited, childrenOnExited)
    } : {});
  }, [childrenOnEnter, childrenOnExited, childrenTabIndex, handleKeyDown, handleEnter, handleExited, hasTransition]);
  var zIndex = !isNumeric(zIndexProp) && zIndexProp !== defaultZIndices && manager.modals.length > 1 ? "".concat(defaultZIndices, ".").concat(zIndexProp) : zIndexProp;
  var modal__css = useMemo(function () {
    return _objectSpread2(_objectSpread2(_objectSpread2({
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'fixed',
      right: 0,
      bottom: 0,
      top: 0,
      left: 0,
      zIndex: zIndex
    }, disableEnforceFocus ? {
      pointerEvents: 'none'
    } : {}), !open && exited ? {
      visibility: 'hidden'
    } : {}), useModalAsBackdrop ? _objectSpread2({
      bg: 'opaqueBlack.3',
      overflow: 'auto',
      variant: 'scrollbar.text-area'
    }, BackdropProps) : null);
  }, [BackdropProps, disableEnforceFocus, exited, open, useModalAsBackdrop, zIndex]);
  if (!keepMounted && !open && (!hasTransition || exited)) {
    return null;
  }

  // if _manager is present, we are using tw modal manager from @costar-ui/share-context
  // we need to wrap the children in a FocusScope to restore focus when the modal is opened
  var content = _manager ?
  /*#__PURE__*/
  // @ts-ignore
  React.createElement(FocusScope, {
    restoreFocus: !disableRestoreFocus,
    contain: open,
    autoFocus: !disableAutoFocus
  }, /*#__PURE__*/React.cloneElement(children, _objectSpread2(_objectSpread2({}, childProps), restChildrenProps))) : /*#__PURE__*/React.createElement(TrapFocus, {
    disableEnforceFocus: disableEnforceFocus,
    disableAutoFocus: disableAutoFocus,
    disableRestoreFocus: disableRestoreFocus,
    getDoc: getDoc,
    isEnabled: isTopModal,
    open: open
  }, /*#__PURE__*/React.cloneElement(children, _objectSpread2(_objectSpread2({}, childProps), restChildrenProps)));
  // eslint-disable-next-line no-console
  // console.log(`------ modal render mounted? ${mounted}, open: ${open} ------`)
  return /*#__PURE__*/React.createElement(Portal, {
    ref: handlePortalRef,
    container: container,
    disablePortal: disablePortal,
    shouldHoistTheme: shouldHoistTheme && !disablePortal
  }, /*#__PURE__*/React.createElement(BoxPrivate, _extends({
    "data-csg-test": "Modal",
    className: "csg-tui-modal",
    ref: handleRef,
    __themeKey: "modals",
    role: "presentation",
    __css: modal__css,
    onClick: useModalAsBackdrop ? handleBackdropClick : undefined
  }, rest), typeof adornerComponent === 'function' ? adornerComponent() : adornerComponent, hideBackdrop || useModalAsBackdrop ? null : /*#__PURE__*/React.createElement(BackdropComponent, _extends({
    open: open,
    onClick: handleBackdropClick
  }, BackdropProps)), content));
});

export default Modal;
//# sourceMappingURL=modal.esm.js.map
